import React, { Fragment, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { ADD_PRODUCT, BRANDS, CATEGORIES, EDIT_PRODUCT, KEEP_ADDING, MODELS, SIZES } from "../../../../utils/constants";
import util from '../../../../utils/miniUtils';
import { toast } from "react-toastify";
import { ProductStore } from "./ProductStore";
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { 
    getCatalogData, 
    getLocations,
    compressImages, 
    createProductFormData, 
    createCatalogItem 
} from "./utils/productModalUtils";
import ImageUploader from "./components/ImageUploader";
import CatalogSelector from "./components/CatalogSelector";
import ProductFormFields from "./components/ProductFormFields";

export default function ProductModal ( { show, product_id, handleClose, action = ADD_PRODUCT, reloadData } ) {
    const MySwal = withReactContent( Swal );

    const {
        categories, brands, models, sizes, locations, moreFields, product, tempValue
    } = ProductStore.useState( state => state );
    const stateSimple = ProductStore.useState( state => state );

    const [loading, setLoading] = useState( false );
    const [keepAdding, setKeepAdding] = useState( {
        alert: false,
        product: null
    } );

    // Show SweetAlert2 dialog when keepAdding.alert is true
    useEffect( () => {
        if ( keepAdding.alert && keepAdding.product ) {
            MySwal.fire( {
                icon: 'success',
                title: 'Producto Creado correctamente.',
                html: `<h4>#${util.zeroPad( keepAdding.product.id, 5 )}</h4>`,
                showCancelButton: true,
                confirmButtonText: 'Agregar otro producto',
                cancelButtonText: 'Regresar a listado',
                customClass: {
                    confirmButton: 'btn btn-warning',
                    cancelButton: 'btn btn-secondary'
                },
                buttonsStyling: false
            } ).then( ( result ) => {
                if ( result.isConfirmed ) {
                    setKeepAdding( { ...keepAdding, alert: false, product: null } );
                } else {
                    handleClose();
                }
            } );
        }
    }, [ keepAdding.alert, keepAdding.product ] );

    const startLoading = () => setLoading( true );
    const stopLoading = () => setLoading( false );

    // Prefix modal title depending on the action
    const titleOfModal = () => {
        switch ( action ) {
            case ADD_PRODUCT:
                return "Agregar Producto";
            case EDIT_PRODUCT:
                return "Editar Producto";
            default:
                return "Agregar Producto";
        }
    };

    // Delete images that already are in the server
    const deleteImageFromServer = async ( id, file ) => {
        try {
            startLoading();
            const request = await util.axios.delete( `images/data/delete/${id}` );
            const { error, msg } = request.data;
            if ( error ) throw msg;
            stopLoading();
            toast.success( `Imagen eliminada` );
            ProductStore.update(  s=>{
                s.imagesServer = Array.from( s.imagesServer ).filter( img => img.id !== file.id );
            } );
            
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    };




    // Get all categories
    useEffect( () => {
        getSelectorsData().then( null );
        if ( action === EDIT_PRODUCT ) {
            getProductInformation().then( null );
        }
    }, [] );

    // Get information to populate selectors
    const getSelectorsData = async () => {
        try {
            startLoading();
            const { categories, brands, models, sizes } = await getCatalogData();
            const locations = await getLocations();
            stopLoading();
            ProductStore.update( s => {
                s.categories = categories;
                s.brands = brands;
                s.models = models;
                s.sizes = sizes;
                s.locations = locations;
            } );
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    };

    // Get information of the product
    const getProductInformation = async () => {
        try {
            startLoading();
            const request = await util.axios.get( `product/data/detail/${product_id}` );
            const { error, msg, data } = request.data;
            if ( error ) throw msg;
            stopLoading();
            ProductStore.update( s => {
                s.product = data;
                // Check if already have some images
                s.imagesServer = data.images;
            } );
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    };

    // Hide input and clean new catalog value
    const cancelNewValue = () => {
        ProductStore.update( s => {
            s.tempValue = "";
            s.showAddCategory = false;
            s.showAddBrand = false;
            s.showAddModel = false;
            s.showAddSize = false;
        } );
    };

    // Saves new value by listening ENTER key
    const pressedEnterKey = async ( e, catalog ) => {
        if ( e.key === "Enter" ) {
            await createNewValueToCatalog( catalog );
        }
    };

    // Create every category, brand, model and size
    const createNewValueToCatalog = async ( catalog ) => {
        try {
            startLoading();
            await createCatalogItem( tempValue, catalog );
            stopLoading();
            cancelNewValue();
            // Ask for categories again
            await getSelectorsData();
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    };

    // Handle changes on input for new catalog item
    const onHandleChangeOfNewValueToCatalog = ( e ) => {
        const { value } = e.target;
        ProductStore.update( s => {
            s.tempValue = value;
        } );
    };

    // Reset the entire store
    const resetStore = ( keep_adding = null ) => {
        ProductStore.update( s => {
            if ( keep_adding === null ) {
                // If keep_adding is NULL it will reset everything
                s.product = {
                    article: "",
                    description: "",
                    status: 1,
                    qty: "",
                    price: "",
                    location_id: "",
                    category_id: 0,
                    brand_id: 0,
                    model_id: 0,
                    year: "",
                    size_id: 0
                };
                s.imagesLocal = [];
                s.imagesServer = [];
                s.selectedFiles = [];
                s.categories = [];
                s.brands = [];
                s.models = [];
                s.sizes = [];
                s.locations = [];
                s.moreFields = false;
                s.showAddCategory = false;
                s.showAddBrand = false;
                s.showAddModel = false;
                s.showAddSize = false;
                s.tempValue = "";
                
            } else{
                // in case user wants to continue adding more similar products
                // We will delete images only but preserve everything else
                s.product = { ...s.product,
                    article: "",
                    description: "",
                    category_id: "",
                    price: "",
                };
                s.imagesLocal = [];
                s.imagesServer = [];
            }
        } );
    };

    // Save product information into the database
    const createProduct = async ( option = null ) => {
        try {
            startLoading();

            // Required field validation (UI)
            if ( !product.location_id ) {
                stopLoading();
                toast.error( 'Selecciona un local de pertenencia' );
                return;
            }
            
            // Compress images before creating FormData
            const compressedImages = await compressImages( stateSimple.imagesLocal );
            
            // Create FormData with product information
            const data = createProductFormData( product, compressedImages, moreFields );

            let request;

            if ( action === EDIT_PRODUCT ) {
                request = await util.axios.put( `product/data/update/${product.id}`, data );
            } else {
                request = await util.axios.post( "product/data/create", data );
            }
            const { error, msg, newProduct } = request.data;
            if ( error ) throw msg;
            
            stopLoading();
            reloadData();
            
            // handleClose();
            if ( action === EDIT_PRODUCT ) {
                resetStore();
                handleClose();
            } else {
                if ( option === KEEP_ADDING ) {
                    util.toast().success( `Producto agregado correctamente` );
                    util.toast().info( `Producto ID #${util.zeroPad( newProduct.id, 5 )}` );
                    resetStore( KEEP_ADDING );
                }else{
                    resetStore();
                    setKeepAdding( { ...keepAdding, alert: true, product: newProduct } );
                    // Ask for categories again
                    await getSelectorsData();
                }
            }
        } catch ( e ) {
            stopLoading();
            util.handleError( e );
        }
    };

    // Handle changes on product object
    const onProductChange = ( e ) => {
        let { name, value } = e.target;
        
        ProductStore.update( s => {
            s["product"][name] = value;
        } );
    };


    return (
        <Fragment>
            <Modal
                show={show}
                onHide={()=> {
                    handleClose();
                    resetStore();
                }}
                backdrop="static"
                keyboard={false}
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {titleOfModal()}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="container text-opacity-75">
                        <div className="row">
                            <div className="col-md-7  col-sm-12">
                                <div className="row">
                                    <div className="col-md-12 mb-2 text-center">
                                        {/* eslint-disable-next-line react/no-unescaped-entities */}
                                        <u className="text-danger"><i>Campos con ' * ' son obligatorios</i></u>
                                    </div>
                                </div>
                                {/*FORM*/}

                                <ProductFormFields 
                                    product={product} 
                                    onProductChange={onProductChange} 
                                    locations={locations}
                                />

                                <CatalogSelector
                                    catalogType={CATEGORIES}
                                    catalogItems={categories}
                                    showAddState="showAddCategory"
                                    productFieldName="category_id"
                                    placeholder="Ingresa nombre de la categoría"
                                    label="Categoría *"
                                    onCreateNewValue={createNewValueToCatalog}
                                    onCancelNewValue={cancelNewValue}
                                    onHandleChangeOfNewValue={onHandleChangeOfNewValueToCatalog}
                                    onPressedEnterKey={pressedEnterKey}
                                    tempValue={tempValue}
                                    product={product}
                                    onProductChange={onProductChange}
                                />

                                <CatalogSelector
                                    catalogType={BRANDS}
                                    catalogItems={brands}
                                    showAddState="showAddBrand"
                                    productFieldName="brand_id"
                                    placeholder="Ingresa nombre de la Marca"
                                    label="Marca"
                                    onCreateNewValue={createNewValueToCatalog}
                                    onCancelNewValue={cancelNewValue}
                                    onHandleChangeOfNewValue={onHandleChangeOfNewValueToCatalog}
                                    onPressedEnterKey={pressedEnterKey}
                                    tempValue={tempValue}
                                    product={product}
                                    onProductChange={onProductChange}
                                />

                                <CatalogSelector
                                    catalogType={MODELS}
                                    catalogItems={models}
                                    showAddState="showAddModel"
                                    productFieldName="model_id"
                                    placeholder="Ingresa nombre del modelo"
                                    label="Modelo"
                                    onCreateNewValue={createNewValueToCatalog}
                                    onCancelNewValue={cancelNewValue}
                                    onHandleChangeOfNewValue={onHandleChangeOfNewValueToCatalog}
                                    onPressedEnterKey={pressedEnterKey}
                                    tempValue={tempValue}
                                    product={product}
                                    onProductChange={onProductChange}
                                />

                                <CatalogSelector
                                    catalogType={SIZES}
                                    catalogItems={sizes}
                                    showAddState="showAddSize"
                                    productFieldName="size_id"
                                    placeholder="Ingresa la medida o tamaño"
                                    label="Medida"
                                    onCreateNewValue={createNewValueToCatalog}
                                    onCancelNewValue={cancelNewValue}
                                    onHandleChangeOfNewValue={onHandleChangeOfNewValueToCatalog}
                                    onPressedEnterKey={pressedEnterKey}
                                    tempValue={tempValue}
                                    product={product}
                                    onProductChange={onProductChange}
                                />
                            </div>
                            <div className="col-md-5 col-sm-12">
                                <p className="text-center"><b>Seleccionar imagenes para este producto</b></p>
                                <div>
                                    <ImageUploader 
                                        fileX="selectedFile1" 
                                        onDeleteImageFromServer={deleteImageFromServer}
                                    />
                                </div>
                                <hr/>
                            </div>

                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-sm" onClick={()=> {
                        handleClose();
                        resetStore();
                    }}>
                        Cancelar
                    </button>
                    <button onClick={createProduct} className="btn btn-primary btn-sm">
                        {action === EDIT_PRODUCT ? "Actualizar" : "Guardar"}
                    </button>
                    {action === ADD_PRODUCT ?
                        <button onClick={()=>createProduct( KEEP_ADDING )} className="btn btn-info btn-sm">
                        Guardar y Agregar más
                        </button>
                        :null}
                </Modal.Footer>
            </Modal>
            {util.LOADING_SCREEN( loading )}
            {/* SweetAlert2 dialog handled by useEffect for keepAdding.alert */}
        </Fragment>
    );
}